import { ActualityHqWithCongestionsRow } from '@warebee/shared/dataset-queries';
import classNames from 'classnames';
import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { TwTheme } from '../../../Tw';
import { analyticsAgent } from '../../AnalyticTracker';
import { getBestContrastColor } from '../../common/color.helper';
import { FormattedValueWithUnit } from '../../common/formatHelper';
import useFormatter from '../../common/useFormatter';
import useGetNamedColor from '../../common/useGetNamedColor';
import ButtonLocateLocation from '../../components/ButtonLocateLocation';
import * as Icon from '../../components/icons';
import useActivityEventProcessTypeDetails from '../../import/activityFeed/schema/useActivityEventProcessTypeDetails';
import { StatMicroInline } from '../../simulation/panels/route/StatInline';
import ActualityEntityTag from './ActualityIEntityTag';

export type EventProcessDetailsProps = {
  index: number;
  event: ActualityHqWithCongestionsRow;
  onHover?: (event: ActualityHqWithCongestionsRow) => void;
  onClick?: (event: ActualityHqWithCongestionsRow) => void;
  serviceType?: 'actuality' | 'simulation';
};

const styleEventPath = 'w-2 rounded-sm';
const styleLegendPosition = 'absolute z-0 ltr:left-5 rtl:right-5';

const colors = TwTheme.extend.colors;

// Helper function to get the correct process type color from theme
const getProcessTypeColor = (processType: string): [string, string] => {
  const processColor =
    colors.ActivityEventProcessType[processType] ||
    colors.ActivityEventProcessType.OTHER;
  return [processColor, getBestContrastColor(processColor)];
};

// const getEventTypeIcon = (eventType: string) => {
//   switch (eventType) {
//     case 'PICK':
//       return Icon.MoveTypeMoveTake;
//     case 'PUT':
//       return Icon.MoveTypeMovePut;
//     case 'JOB_START':
//       return Icon.ArrowRight;
//     case 'JOB_END':
//       return Icon.BoxX;
//     default:
//       return Icon.CircleInfo;
//   }
// };

export const EventProcessDetails: React.FC<
  EventProcessDetailsProps
> = props => {
  const { t } = useTranslation('feed');
  const formatter = useFormatter();
  const getNamedColor = useGetNamedColor();
  const { event } = props;

  const eventProcessTypeDetails = useActivityEventProcessTypeDetails();
  const eventProcessTypeOptions = Object.fromEntries(
    eventProcessTypeDetails.map(d => [d.id, d.title]),
  );
  const EventIcon =
    eventProcessTypeDetails.find(d => d.id === event.eventProcessType)?.icon ||
    Icon.CircleInfo;

  const processColor = getProcessTypeColor(event.eventProcessType);
  const eventColor = getProcessTypeColor(event.eventProcessType);

  const duration = formatter.formatTimeSpan(event.duration, 1);
  const cost = event.cost ? formatter.formatCurrency(event.cost) : null;

  let distance: FormattedValueWithUnit;
  let weight: FormattedValueWithUnit;
  let volume: FormattedValueWithUnit;

  if (event.netWeight && event.quantity) {
    weight = formatter.formatWeight((event.netWeight * event.quantity) / 1000);
  }

  if (event.volume && event.quantity) {
    volume = formatter.formatVolume((event.volume * event.quantity) / 1000000);
  }

  if (event.distance) {
    distance = formatter.formatDistance(event.distance);
  }

  console.log('event', event);

  return (
    <div
      data-component={`EventProcess${props.serviceType}`}
      className={classNames(
        'overflow-hidden',
        'group cursor-pointer',
        'relative text-xs',
        'divide-menu-600/50 bg-app-panel-dark/75 hover:bg-brand divide-x hover:bg-opacity-5',
      )}
      onMouseEnter={() => props.onHover?.(event)}
      onMouseLeave={() => props.onHover?.(null)}
      onClick={() => props.onClick?.(event)}
    >
      <div
        className={classNames(
          'flex flex-1 flex-col items-center',
          'min-h-[2.5rem] px-2 text-xs xl:text-sm',
          'border-menu-400/20 border-b border-solid',
        )}
      >
        <div
          data-component="EventProcessLegend"
          className={classNames(
            `any-hover:group-hover:border-menu-active my-px h-full`,
            styleEventPath,
            styleLegendPosition,
          )}
          style={{
            backgroundColor: processColor?.[0],
          }}
        />

        {props.index > 0 && (
          <div
            data-component="EventProcessCounter"
            className={classNames(
              'flex items-center justify-center',
              'text-xxs text-menu-text font-bold',
              'z-100 absolute h-6 w-10 ltr:left-1 rtl:right-1',
              'top-1',
              'rounded-full',
              'border-menu-700 any-hover:group-hover:border-menu-300 border',
              'bg-app-panel-dark',
            )}
            style={{
              color: processColor?.[1],
              borderColor: processColor?.[0],
            }}
            title={t(`Job Line: {{jobLine}}`, {
              jobLine: event.jobLine,
            })}
          >
            {props.index}
          </div>
        )}

        <span
          data-component="EventProcessTitle"
          onClick={() => {
            props.onClick(event);
            try {
              analyticsAgent?.track(`Event: [${event.eventType}]`);
            } catch (ex) {
              console.debug('analyticsAgent Error:', ex);
            }
          }}
          title={eventProcessTypeOptions[event.eventProcessType]}
          // data-meta={_.map(
          //   event?.details,
          //   (value, key) => `${key}: ${value}`,
          // ).join(',')}
          className={classNames(
            'w-full flex-1',
            'pt-2 ltr:mr-2 ltr:pl-12 rtl:ml-2 rtl:pr-12',
            'truncate text-sm',
            'text-menu-text/60 any-hover:group-hover:text-menu-active',
            'select-text',
          )}
        >
          <span className="flex w-full items-center gap-1">
            <ActualityEntityTag
              iconDropMenu={<EventIcon className="h-4 w-4 fill-current" />}
              entity={'event'}
              title={`${eventProcessTypeOptions[event.eventProcessType]}`}
              filters={{
                eventId: event.eventId,
              }}
              className="bg-transparent text-xs"
            />

            {/* <span>{eventProcessTypeOptions[event.eventProcessType]}</span> */}
            {/* <span className="mx-1">{t`at`}</span> */}
          </span>

          {(event.locationId || event.layoutLocationId) && (
            <div className="mt-1">
              <ButtonLocateLocation
                locationId={event.locationId || event.layoutLocationId}
              />
            </div>
          )}
        </span>

        <div
          data-component="processMinStatContainer"
          className={classNames(
            'flex flex-1 flex-wrap items-center',
            'w-full py-2',
            'space-x-0.5 xl:space-x-1',
            'ltr:pl-12 rtl:pr-12',
            'text-menu-text',
          )}
        >
          {!_.isNil(event.quantity) && event.quantity > 0 && (
            <StatMicroInline
              title={t`Units`}
              value={`${event.quantity}`}
              unitOfMeasure={`${event.uom}`}
            />
          )}

          {!_.isNil(distance) && distance.raw > 0 && (
            <StatMicroInline
              title={t`Distance`}
              value={`${distance.value}`}
              unitOfMeasure={`${distance.unit}`}
            />
          )}
          {!_.isNil(weight) && weight.raw > 0 && (
            <StatMicroInline
              title={t`Weight`}
              value={`${event.netWeight}`}
              unitOfMeasure={formatter.formatWeight(event.netWeight).unit}
            />
          )}
          {!_.isNil(volume) && volume.raw > 0 && (
            <StatMicroInline
              title={t`Volume`}
              value={`${volume.value}`}
              unitOfMeasure={volume.unit}
            />
          )}
          {!_.isNil(duration) && duration.raw > 0 && (
            <StatMicroInline
              title={t`Duration`}
              value={`${duration.value}`}
              unitOfMeasure={duration.unit}
            />
          )}
          {!_.isNil(cost) && cost.raw > 0 && (
            <StatMicroInline
              title={t`Cost`}
              value={
                cost.raw === 0
                  ? '—'
                  : cost.raw < 1
                    ? `< ${cost.raw.toPrecision(1)}`
                    : `${cost.value}`
              }
              unitOfMeasure={cost.unit}
            />
          )}
        </div>
      </div>
    </div>
  );
};
